import { ModelBase } from './ModelBase';

export class GameModel extends ModelBase {
  constructor(data) {
    super('GameModel');
    this._godmode = false;
    this._infiniteSpecial = false;
    this._noEnemyAttacks = false;
    this._gameSettings = SettingsModel(data.game_settings);
    this._enemyData = data.enemies.map((e) => EnemyModel(e));
    this._herodata = data.heroes.map((e) => HeroModel(e));

    this._leveldata = data.levels.map((e) => LevelModel(e));
    this._pickupdata = data.pickups.map((e) => PickupModel(e));
    this._weapondata = data.weapons.map((e) => WeaponModel(e));

    this.showDebug = __WATCH__;
    this._showDebugCharacterStates = true;
    this._showDebugCollisionMap = false;
    this._showDebugCollisionColliders = true;

    this._fooddata = [
      { id: 'pizza', spriteid: 'pickup_food_pizza', health: 50 },
      { id: 'waffle', spriteid: 'pickup_food_waffle', health: 25 },
    ];
    this._stagedata = this._leveldata.reduce((arr, m) => {
      arr = [...arr, ...m.stages];
      return arr;
    }, []);
    this._combatzonedata = data.combat_zones.map((e) => CombatZoneModel(e));
    this.log('Created', this);
  }

  get showDebugCollisionColliders() {
    return this.showDebug && this._showDebugCollisionColliders;
  }
  get showDebugCollisionMap() {
    return this.showDebug && this._showDebugCollisionMap;
  }
  get showDebugCharacterStates() {
    return this.showDebug && this._showDebugCharacterStates;
  }

  get godmode() {
    return this._godmode;
  }
  set godMode(value) {
    this._godmode = value;
  }

  get noEnemyAttacks() {
    return this._noEnemyAttacks;
  }

  getWeaponIds() {
    return this._weapondata.map((w) => w.id);
  }

  getWeapons() {
    return [...this._weapondata];
  }

  getPickupConfig(pickupid) {
    return this._pickupdata.find((p) => p.id === pickupid);
  }

  getWeaponConfig(weaponid) {
    const result = this._weapondata.find((p) => p.id === weaponid);
    return result ? { ...result } : null;
  }

  getCombatZone(zoneid) {
    return this._combatzonedata.find((z) => z.id === zoneid);
  }

  getFoodItemConfig(foodid) {
    return this._fooddata.find((f) => f.id === foodid);
  }

  getEnemy(enemyid) {
    return _findInArray(this._enemyData, enemyid);
  }

  getEnemies() {
    return this._enemyData;
  }

  getEnemyIds() {
    return this._enemyData.map((e) => e.id);
  }

  getHeroes() {
    return [...this._herodata];
  }

  getHeroConfig(heroid) {
    return _findInArray(this._herodata, heroid);
  }

  getFreeHeroes() {
    return this._herodata.filter((h) => !h.cost).map((h) => h.id);
  }

  getUnlockableComboMoves() {
    return this._herodata.reduce((arr, hero) => {
      const data = {
        heroid: hero.id,
        moves: hero.attackFinishers.filter((a) => a.cost > 0),
      };
      arr.push(data);
      return arr;
    }, []);
  }

  getStoryLevels() {
    return [...this._leveldata];
  }

  getStoryStages() {
    return this._stagedata.map((m) => m);
  }

  getStoryStagePosition(stageid) {
    for (let l = 0; l < this._leveldata.length; l++) {
      const s = this._leveldata[l].stages.findIndex((s) => s.id === stageid);
      if (s >= 0) {
        return { level: l + 1, stage: s + 1 };
      }
    }
  }

  getLevelConfig(levelid) {
    const result = _findInArray(this._leveldata, levelid);
    if (result) return { ...result };
  }

  getStageConfig(stageid) {
    const result = _findInArray(this._stagedata, stageid);
    if (result) return { ...result };
  }

  canPurchaseHero(heroid, coins) {
    return this.getHeroConfig(heroid).cost <= coins;
  }

  calculateLevelResults(results, player) {
    const r = {
      coins: results.coins || 0,
      damage: results.damage || 0,
      time: results.time || 0,
      lives: results.lives || 0,
    };
    r.score = r.coins + r.damage + r.time + r.lives;
    return r;
  }

  get specialPointsCap() {
    return this._gameSettings.specialPointsCap;
  }

  get pointsPerCoin() {
    return this._gameSettings.pointsPerCoin;
  }

  get pointsPerDamageDealt() {
    return this._gameSettings.pointsPerDamageDealt;
  }

  get pointsPerSecondsRemaining() {
    return this._gameSettings.pointsPerSecondRemain;
  }

  get pointsPerLivesRemaining() {
    return this._gameSettings.pointsPerLivesRemain;
  }
}
// =============================================================================================
// HELPERS
// =============================================================================================

const _findInArray = (array, id) => {
  return array.find((a) => a.id === id);
};

const PickupModel = (props) => ({
  id: props.external_id,
  type: props.data.type,
  health: props.data.health,
  sprite: props.data.sprite_id,
  pickupsfx: props.data.pickupsfx,
});

const WeaponModel = (props) => ({
  id: props.external_id,
  name: props.name,
  animation: props.data.animation,
  damage: parseFloat(props.data.damage),
  skin: props.data.skin,
  sprite: props.data.sprite,
  uses: parseFloat(props.data.uses),
  pickupsfx: props.data.pickupsfx,
  damagesfx: props.data.contactsfx,
  skins: props.data.skins.map((s) => ({
    id: s.skin,
    skin: s.skin,
    sprite: s.sprite,
    animation: s.animation || false,
    cost: parseFloat(s.cost) || false,
    uses: s.uses > 0 ? parseFloat(s.uses) : false,
    damage: s.damage > 0 ? parseFloat(s.damage) : false,
  })),
});

const SettingsModel = (props) => ({
  specialPointsCap: parseFloat(props.special_point_cap),
  pointsPerCoin: parseFloat(props.points_per_coin),
  pointsPerDamageDealt: parseFloat(props.points_per_damage),
  pointsPerSecondRemain: parseFloat(props.points_per_second),
  pointsPerLivesRemain: parseFloat(props.points_per_life),
  lifesPerStage: parseFloat(props.lifes_per_stage),
});

const HeroModel = (props) => ({
  id: props.external_id,
  spineid: props.data.spine_id,
  cost: parseFloat(props.data.cost),
  health: parseFloat(props.data.health),
  speed: parseFloat(props.data.speed),
  knockdown: 2.5,
  attackRange: parseFloat(props.data.attack_range),
  grabRange: parseFloat(props.data.grab_range),
  basicAttacks: props.data.basic_attacks.map((a) => AttackModel({ ...a, stagger: true })),
  grabAttacks: props.data.grab_attacks.map((a) => AttackModel({ ...a, stagger: true })),
  attackFinishers: props.data.attack_finishers.map((a, i) => ({
    ...AttackModel(a),
    knockdown: true,
    id: i.toString(),
  })),
  grabFinishers: props.data.grab_finishers.map((a, i) => ({ ...AttackModel(a), knockdown: true })),
  arialAttacksStanding: AttackModel({ ...props.data.standing_arial, stagger: true }),
  arialAttacksForward: AttackModel(props.data.forward_arial),
  specialAttackForward: { ...AttackModel(props.data.forward_special), knockdown: true },
  specialAttackStanding: { ...AttackModel(props.data.standing_special), knockdown: true },
  bodyCollider:
    props.data.bodycollider?.type === '1'
      ? {
          width: parseFloat(props.data.bodycollider.width),
          height: parseFloat(props.data.bodycollider.height),
          offsetX: parseFloat(props.data.bodycollider.offsetx) || 0,
          offsetY: parseFloat(props.data.bodycollider.offsety) || 0,
        }
      : false,
});

const EnemyModel = (props) => ({
  id: props.external_id,
  name: props.name,
  spineid: props.data.spine_id,
  combatStyle: props.data.combat_style || 'MELEE',
  speed: parseFloat(props.data.speed),
  health: parseFloat(props.data.health),
  coins: parseFloat(props.data.coins),
  stagger: parseFloat(props.data.stagger),
  knockdown: parseFloat(props.data.knockdown),
  attackRate: parseFloat(props.data.attack_rate),
  attackRange: parseFloat(props.data.attack_range),
  attacks: props.data.attacks.map((a) => AttackModel(a)),
  bodyCollider:
    props.data.bodycollider?.type === '1'
      ? {
          width: parseFloat(props.data.bodycollider.width),
          height: parseFloat(props.data.bodycollider.height),
          offsetX: parseFloat(props.data.bodycollider.offsetx) || 0,
          offsetY: parseFloat(props.data.bodycollider.offsety) || 0,
        }
      : false,
});

const LevelModel = (props) => ({
  id: props.external_id,
  cutsceneEnd: props.data.end_cutscene,
  stages: props.data.stages.map((s) => ({
    id: s.external_id,
    mapid: s.mapid,
    timeLimit: parseFloat(s.time_limit),
  })),
});

const CombatZoneModel = (props) => ({
  id: props.external_id,
  boss: props.data.boss || false,
  bossWaveDelay: parseFloat(props.data.boss_wave_delay) || 10,
  enemyWaves: props.data.enemy_waves.map((w) =>
    w.spawns.map((s) => ({ enemyid: s.enemy, qty: parseFloat(s.qty), direction: s.direction }))
  ),
});

const AttackModel = (props) => ({
  name: props.name,
  animation: props.animation,
  cooldown: parseFloat(props.cooldown) || 0,
  damage: parseFloat(props.damage) || 0,
  knockdown: props.knockdown === '1',
  stagger: props.stagger || false,
  cost: parseFloat(props.cost) || 0,
  specialPoints: parseFloat(props.special_points) || 0,
  type: props.type || 'default',
  class: props.attackclass,
});
